/**
 * Settings → Connected apps (/api/assistant/connected-apps).
 *
 * What can reach this workspace as an AI app, and the revoke that stops it.
 * Two credential kinds arrive as one list — a signed-in host and a pasted key
 * — because "what can reach my business" is one question. Nothing here ever
 * carries a token or a key: the server returns none, so there is nothing to
 * unwrap into the render tree but names, dates and plain-language lines.
 */
import { apiRequest } from './core';
import { unwrap, type Envelope } from './assistantApi';

export type ConnectionKind = 'oauth' | 'api_key';

export interface ConnectedApp {
  id: string;
  kind: ConnectionKind;
  /** What the merchant recognises: "Claude Code", "Codex CLI", or a key's name. */
  displayName: string;
  scopes: string[];
  /** Plain-language permission lines, as the consent screen worded them. */
  permissions: string[];
  createdAt: string;
  lastUsedAt: string | null;
  authorizedByUserId: string | null;
  /** `device_code` for a terminal sign-in, `authorization_code` for a browser one; absent for a key. */
  grantKind?: string;
}

export const connectedAppsApi = {
  async list(): Promise<ConnectedApp[]> {
    const data = unwrap(
      await apiRequest<Envelope<{ apps?: ConnectedApp[] }>>('/assistant/connected-apps'),
    );
    return data.apps ?? [];
  },

  /** Revoke bites at once: the next request on that credential fails. */
  async revoke(id: string, kind: ConnectionKind): Promise<void> {
    unwrap(
      await apiRequest<Envelope<{ revoked?: boolean }>>(
        `/assistant/connected-apps/${encodeURIComponent(id)}?kind=${kind}`,
        { method: 'DELETE' },
      ),
    );
  },
};
